import { select, takeEvery } from 'redux-saga/effects';

import { store } from '@redux/storeConfig/store';
import camelcaseKeys from 'camelcase-keys';
import { normalize } from 'normalizr';
import * as actions from '../actions';
import * as selectors from '../reducers';
import * as schemas from '../schemas';
import {
  deleteProtocolService,
  getProtocolsListService,
  saveProtocolService
} from '../services/protocols';
import * as types from '../types';

/* -------------------------------------------------------------------------- */
/*                                    FETCH                                   */
/* -------------------------------------------------------------------------- */

function* fetchProtocolsList(action) {
  const { consoleId } = action.payload;
  getProtocolsListService(
    { consoleId },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        const {
          entities: { protocols },
          result
        } = normalize(resultData, schemas.protocols);
        store.dispatch(
          actions.completeFetchingProtocols(protocols, result)
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingProtocols(error));
      }
    }
  );
}

export function* watchFetchProtocolsList() {
  yield takeEvery(types.PROTOCOLS_FETCH_STARTED, fetchProtocolsList);
}

/* -------------------------------------------------------------------------- */
/*                             PARTIAL FETCH LIST                             */
/* -------------------------------------------------------------------------- */

function* partialFetchProtocolsList(action) {
  const { consoleId } = action.payload;

  getProtocolsListService(
    { consoleId },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        const {
          entities: { protocols },
          result
        } = normalize(resultData, schemas.protocols);
        store.dispatch(
          actions.completePartialFetchingProtocols(protocols, result)
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failPartialFetchingProtocols(error));
      }
    }
  );
}

export function* watchPartialFetchProtocolsList() {
  yield takeEvery(types.PROTOCOLS_PARTIAL_FETCH_STARTED, partialFetchProtocolsList);
}

/* -------------------------------------------------------------------------- */
/*                                     ADD                                    */
/* -------------------------------------------------------------------------- */

function* addProtocol(action) {
  const protocol = action.payload;
  saveProtocolService(
    {
      ...protocol,
      protocolId: null
    },
    {
      successAction: (response) => {
        const addedProtocol = camelcaseKeys(response?.data[0]);
        store.dispatch(actions.completeAddingProtocol(protocol.protocolId, addedProtocol));
      },
      errorAction: (error) => {
        store.dispatch(actions.failAddingProtocol(protocol.protocolId, error));
      }
    }
  );
}

export function* watchAddProtocolStarted() {
  yield takeEvery(types.PROTOCOLS_ADD_STARTED, addProtocol);
}

/* -------------------------------------------------------------------------- */
/*                                    EDIT                                    */
/* -------------------------------------------------------------------------- */

function* editProtocol(action) {
  const protocol = action.payload;
  const oldProtocol = yield select((state) => selectors.getProtocol(state, protocol.protocolId));
  saveProtocolService(
    {
      ...protocol
    },
    {
      successAction: (response) => {
        const editedProtocol = camelcaseKeys(response?.data[0]);
        store.dispatch(actions.completeEditingProtocol(editedProtocol));
      },
      errorAction: (error) => {
        store.dispatch(actions.failEditingProtocol(oldProtocol, error));
      }
    }
  );
}

export function* watchEditProtocolStarted() {
  yield takeEvery(types.PROTOCOLS_EDIT_STARTED, editProtocol);
}

/* -------------------------------------------------------------------------- */
/*                                   REMOVE                                   */
/* -------------------------------------------------------------------------- */

function* removeProtocol(action) {
  const { protocolId } = action.payload;
  const protocol = yield select((state) => selectors.getProtocol(state, protocolId));
  deleteProtocolService(
    { protocolId, protocolName: protocol.protocolName },
    {
      successAction: (response) => {
        store.dispatch(actions.completeRemovingProtocol(protocolId));
      },
      errorAction: (error) => {
        store.dispatch(actions.failRemovingProtocol(protocolId, error));
      }
    }
  );
}

export function* watchRemoveProtocolStarted() {
  yield takeEvery(types.PROTOCOLS_REMOVE_STARTED, removeProtocol);
}
